import { useMemo, useState } from 'react';

export type OutboxItem = {
  id?: string;
  submission_id?: string;
  to_email?: string;
  subject?: string;
  status?: string;
  attempts?: number;
  last_error?: string | null;
  created_at?: string;
  updated_at?: string;
};

const ESTADOS = ['pending', 'manual', 'sent', 'failed'] as const;
type Estado = (typeof ESTADOS)[number];

const ESTADO_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  manual: 'Manual',
  sent: 'Enviado',
  failed: 'Falló',
};

const fecha = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/** Mantiene 2 caracteres del usuario; el dominio queda visible para operar. */
export function enmascararEmail(valor: string | undefined): string {
  if (!valor) return '—';
  const arroba = valor.lastIndexOf('@');
  if (arroba < 1) return '•••';
  const local = valor.slice(0, arroba);
  const dominio = valor.slice(arroba + 1);
  const visible = local.slice(0, 2);
  const ocultos = '•'.repeat(Math.min(6, Math.max(2, local.length - visible.length)));
  return `${visible}${ocultos}@${dominio}`;
}

function formatearFecha(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : fecha.format(d);
}

export function MesaOutbox({ items }: { items: OutboxItem[] }) {
  const [filtro, setFiltro] = useState<'todos' | Estado>('todos');

  const conteos = useMemo(() => {
    const base: Record<string, number> = { todos: items.length };
    for (const estado of ESTADOS) base[estado] = 0;
    for (const item of items) {
      const estado = item.status ?? 'pending';
      base[estado] = (base[estado] ?? 0) + 1;
    }
    return base;
  }, [items]);

  const visibles = useMemo(
    () => (filtro === 'todos' ? items : items.filter((i) => (i.status ?? 'pending') === filtro)),
    [items, filtro],
  );

  if (items.length === 0) {
    return (
      <div className="mesa-vacio">
        <p className="mesa-vacio__titulo">No hay emails en la cola</p>
        <p className="mesa-vacio__texto">
          Cada autoevaluación completada encola su perfil. Cuando llegue la primera, aparece acá.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mesa-filtros" role="group" aria-label="Filtrar por estado">
        {(['todos', ...ESTADOS] as const).map((clave) => (
          <button
            key={clave}
            type="button"
            className="mesa-filtro"
            aria-pressed={filtro === clave}
            onClick={() => setFiltro(clave)}
          >
            {clave === 'todos' ? 'Todos' : ESTADO_LABEL[clave]}
            <span className="mesa-filtro__n">{conteos[clave] ?? 0}</span>
          </button>
        ))}
      </div>

      {visibles.length === 0 ? (
        <div className="mesa-vacio">
          <p className="mesa-vacio__titulo">Sin emails en este estado</p>
          <p className="mesa-vacio__texto">Probá con otro filtro para ver el resto de la cola.</p>
        </div>
      ) : (
        <div className="mesa-tabla-wrap">
          <table
            className="mesa-tabla"
            aria-label="Cola de emails: estado, destinatario enmascarado, asunto, intentos y fecha de alta"
          >
            <thead>
              <tr>
                <th scope="col">Estado</th>
                <th scope="col">Destinatario</th>
                <th scope="col">Asunto</th>
                <th scope="col" className="mesa-tabla__num">
                  Intentos
                </th>
                <th scope="col">Alta</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((item, i) => {
                const estado = item.status ?? 'pending';
                return (
                  <tr key={item.id ?? `${item.submission_id ?? 'row'}-${i}`}>
                    <td data-label="Estado">
                      <span className={`mesa-estado mesa-estado--${estado}`}>
                        {ESTADO_LABEL[estado] ?? estado}
                      </span>
                    </td>
                    <td data-label="Destinatario">
                      <span className="mesa-destinatario" title="Email enmascarado">
                        {enmascararEmail(item.to_email)}
                      </span>
                    </td>
                    <td data-label="Asunto" className="mesa-tabla__asunto">
                      {item.subject ?? '—'}
                      {item.last_error && (
                        <span className="mesa-detalle-error">{item.last_error}</span>
                      )}
                    </td>
                    <td data-label="Intentos" className="mesa-tabla__num">
                      {item.attempts ?? 0}
                    </td>
                    <td data-label="Alta" className="mesa-tabla__fecha">
                      {formatearFecha(item.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
